import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Clock, Calendar } from 'lucide-react';
import { InlineWidget } from 'react-calendly';

const CALENDLY_URL = "https://calendly.com/valoryx-info/30min";
const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Unsplash thumbnail helper - use full URL format for reliability */
const u = (id) => `https://images.unsplash.com/photo-${id}?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=180&w=280&q=60`;

const QUESTIONS = [
  {
    id: 'ziel',
    number: 1,
    question: 'Was möchten Sie mit uns erreichen?',
    type: 'list',
    multi: false,
    options: [
      { key: 'A', label: 'Mehr qualifizierte Erstgespräche pro Monat' },
      { key: 'B', label: 'Einen planbaren Vertriebskanal aufbauen' },
      { key: 'C', label: 'Interne Vertriebsressourcen entlasten' },
      { key: 'D', label: 'Neue Märkte oder Zielgruppen erschließen' },
      { key: 'E', label: 'Bisherige Agentur ersetzen' },
    ],
  },
  {
    id: 'branche',
    number: 2,
    question: 'Welche Branche beschreibt Ihr Unternehmen am besten?',
    type: 'image-cards',
    multi: false,
    options: [
      { key: 'A', label: 'Software / SaaS', img: u('1460925895917-afdab827c52f') },
      { key: 'B', label: 'Beratung / Consulting', img: u('1642522029691-029b5a432954') },
      { key: 'C', label: 'IT-Dienstleistungen', img: u('1504868584819-f8e8b4b6d7e3') },
      { key: 'D', label: 'Agentur / Kreativ', img: u('1599658880436-c61792e70672') },
      { key: 'E', label: 'Industrie / Handel', img: u('1642522029686-5485ea7e6042') },
      { key: 'F', label: 'Andere B2B-Branche', img: u('1762341119317-fb5417c18407') },
    ],
  },
  {
    id: 'auftragswert',
    number: 3,
    question: 'In welcher Größenordnung liegt Ihr durchschnittlicher Auftragswert?',
    type: 'list',
    multi: false,
    options: [
      { key: 'A', label: 'Unter 2.000 EUR' },
      { key: 'B', label: '2.000 - 5.000 EUR' },
      { key: 'C', label: '5.000 - 15.000 EUR' },
      { key: 'D', label: '15.000 - 50.000 EUR' },
      { key: 'E', label: 'Über 50.000 EUR' },
    ],
  },
  {
    id: 'teamgroesse',
    number: 4,
    question: 'Wie groß ist Ihr Team aktuell?',
    type: 'cards',
    multi: false,
    options: [
      { key: 'A', label: '1-5 Personen' },
      { key: 'B', label: '6-20 Personen' },
      { key: 'C', label: '21-50 Personen' },
      { key: 'D', label: '51-200 Personen' },
      { key: 'E', label: '200+' },
    ],
  },
  {
    id: 'vertriebskanal',
    number: 5,
    question: 'Über welche Kanäle gewinnen Sie heute Neukunden?',
    subtitle: 'Mehrfachauswahl möglich',
    type: 'image-cards',
    multi: true,
    options: [
      { key: 'A', label: 'Empfehlungen & Netzwerk', img: u('1758520144427-ddb02ac74e9d') },
      { key: 'B', label: 'LinkedIn / Social Media', img: u('1762340274849-87b569b2f0c8') },
      { key: 'C', label: 'Outbound / Telefonakquise', img: u('1525182008055-f88b95ff7980') },
      { key: 'D', label: 'Online-Werbung (Google, Meta)', img: u('1769596722257-282ec3fe8594') },
      { key: 'E', label: 'Inbound / Content Marketing', img: u('1599658880436-c61792e70672') },
      { key: 'F', label: 'Events & Messen', img: u('1761195689615-9469b65dac01') },
    ],
  },
  {
    id: 'wunsch_termine',
    number: 6,
    question: 'Wie viele qualifizierte Gespräche pro Monat wünschen Sie sich?',
    type: 'cards',
    multi: false,
    options: [
      { key: 'A', label: '5-10' },
      { key: 'B', label: '10-20' },
      { key: 'C', label: '20-30' },
      { key: 'D', label: '30+' },
    ],
  },
  {
    id: 'region',
    number: 7,
    question: 'Welchen Markt möchten Sie adressieren?',
    type: 'cards',
    multi: false,
    options: [
      { key: 'A', label: 'Regional / Lokal' },
      { key: 'B', label: 'Deutschland' },
      { key: 'C', label: 'DACH-Raum' },
      { key: 'D', label: 'Europa' },
      { key: 'E', label: 'International' },
    ],
  },
  {
    id: 'zeitrahmen',
    number: 8,
    question: 'Wann möchten Sie mit der Neukundengewinnung starten?',
    type: 'list',
    multi: false,
    options: [
      { key: 'A', label: 'Sofort — so schnell wie möglich' },
      { key: 'B', label: 'In den nächsten 2 Wochen' },
      { key: 'C', label: 'Innerhalb der nächsten 1-3 Monate' },
      { key: 'D', label: 'In 3-6 Monaten' },
      { key: 'E', label: 'Ich informiere mich erst' },
    ],
  },
  {
    id: 'erwartung',
    number: 9,
    question: 'Was ist Ihnen bei einem Vertriebspartner besonders wichtig?',
    type: 'text',
    placeholder: 'z.B. Branchenverständnis, Transparenz, schnelle Umsetzung...',
  },
  {
    id: 'email',
    number: 10,
    question: 'Ihre geschäftliche E-Mail Adresse *',
    type: 'email',
    placeholder: 'name@unternehmen.de',
    required: true,
  },
  {
    id: 'kontakt',
    number: 11,
    question: 'Wie erreichen wir Sie am besten? *',
    type: 'contact',
    required: true,
  },
];


/* =========== PRELANDER =========== */
function Prelander({ onStart }) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 text-center relative overflow-hidden bg-white"
      data-testid="quiz-prelander"
    >
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-[0.04]"
        style={{ background: 'radial-gradient(circle, #2563EB 0%, transparent 70%)' }}
      />
      <div className="relative z-10 max-w-xl">
        <div className="mb-10">
          <span className="text-slate-900 font-black text-xl tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            VALORYX
          </span>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-200 bg-blue-50 mb-8">
          <Clock className="w-3.5 h-3.5 text-blue-600" />
          <span className="text-xs font-bold text-blue-700 tracking-wide">Dauert nur 90 Sekunden</span>
        </div>
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-[1.1] mb-5 font-black tracking-tighter"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Erfahren Sie, ob unser Modell zu Ihnen passt
        </h1>
        <p className="text-base text-slate-600 leading-relaxed max-w-md mx-auto mb-10" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Beantworten Sie wenige kurze Fragen und wir zeigen Ihnen, wie wir qualifizierte Neukundentermine für Ihr Unternehmen generieren können.
        </p>
        <button
          onClick={onStart}
          data-testid="quiz-start-btn"
          className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white font-bold text-base rounded-md hover:bg-blue-700 transition-all cursor-pointer"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          Potenzialanalyse starten
          <ArrowRight className="w-5 h-5" />
        </button>
        <p className="text-xs text-slate-400 mt-4">Kostenlos & unverbindlich</p>
      </div>
      <footer className="absolute bottom-6 left-0 right-0 text-center text-slate-300 text-xs px-6">
        2026 VALORYX.{' '}
        <Link to="/impressum" className="underline hover:text-slate-500">Impressum</Link>
        {' - '}
        <Link to="/datenschutz" className="underline hover:text-slate-500">Datenschutzerklärung</Link>
      </footer>
    </div>
  );
}


/* =========== IMAGE CARD COMPONENT =========== */
function ImageCard({ opt, selected, onClick, questionId }) {
  return (
    <button
      data-testid={`quiz-option-${questionId}-${opt.key}`}
      onClick={onClick}
      className={`group relative rounded-xl overflow-hidden text-left transition-all duration-200 border-2 ${
        selected
          ? 'border-[#3FA7DB] shadow-md ring-1 ring-[#3FA7DB]/30'
          : 'border-transparent hover:shadow-sm'
      } bg-white`}
    >
      {/* Image */}
      <div className="w-full h-16 sm:h-24 overflow-hidden bg-gray-100">
        <img
          src={opt.img}
          alt=""
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      {/* Selected check */}
      {selected && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#3FA7DB] flex items-center justify-center shadow-sm">
          <Check className="w-3.5 h-3.5 text-white" />
        </div>
      )}
      {/* Label */}
      <div className="px-3 py-2 sm:px-4 sm:py-3 flex items-center gap-2">
        <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold shrink-0 ${
          selected ? 'bg-[#3FA7DB] text-white' : 'bg-[#e8e8e8] text-[#999]'
        }`}>
          {opt.key}
        </span>
        <span className="text-xs sm:text-sm font-medium text-[#061620] leading-tight">{opt.label}</span>
      </div>
    </button>
  );
}


/* =========== MAIN QUIZ =========== */
export default function QuizPage() {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [textValue, setTextValue] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [errors, setErrors] = useState({});

  const current = QUESTIONS[step];
  const total = QUESTIONS.length;
  const progress = ((step + 1) / total) * 100;

  const setAnswer = useCallback((questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setErrors(prev => ({ ...prev, [questionId]: null }));
  }, []);

  const goNext = useCallback(() => {
    if (step < total - 1) {
      setStep(s => s + 1);
      setTextValue('');
    }
  }, [step, total]);

  const goPrev = useCallback(() => {
    if (step > 0) setStep(s => s - 1);
  }, [step]);

  const handleChoiceClick = (questionId, optionLabel, multi) => {
    if (multi) {
      const cur = answers[questionId] || [];
      const updated = cur.includes(optionLabel)
        ? cur.filter(v => v !== optionLabel)
        : [...cur, optionLabel];
      setAnswer(questionId, updated);
    } else {
      setAnswer(questionId, optionLabel);
      setTimeout(goNext, 350);
    }
  };

  const handleOk = () => {
    if (current.type === 'text') {
      setAnswer(current.id, textValue);
      goNext();
    } else if (current.type === 'email') {
      if (!emailValue || !emailValue.includes('@') || !emailValue.includes('.')) {
        setErrors(prev => ({ ...prev, email: 'Bitte gib eine gültige E-Mail Adresse ein' }));
        return;
      }
      setAnswer(current.id, emailValue);
      goNext();
    }
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!contactName.trim()) newErrors.name = 'Pflichtfeld';
    if (!contactLastName.trim()) newErrors.lastName = 'Pflichtfeld';
    if (!contactPhone.trim()) newErrors.phone = 'Pflichtfeld';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const finalAnswers = {
      ...answers,
      kontakt: { name: contactName, lastName: contactLastName, phone: contactPhone },
    };
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/quiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalAnswers),
      });
    } catch (e) {
      console.error('Quiz submit error:', e);
    }
    setSubmitting(false);
    setSubmitted(true);

    // Fire Facebook Pixel Lead event with Advanced Matching data
    try {
      if (typeof window.fbq === 'function') {
        // Update pixel with user data for better matching (improves Event Match Quality)
        window.fbq('init', '534398925768793', {
          em: emailValue.toLowerCase().trim(),
          fn: contactName.toLowerCase().trim(),
          ln: contactLastName.toLowerCase().trim(),
          ph: contactPhone.replace(/[\s\-\+\(\)]/g, ''),
        });
        window.fbq('track', 'Lead');
      }
    } catch (e) {
      console.error('fbq Lead event error:', e);
    }
  };

  const canProceedMulti = current?.multi && (answers[current.id]?.length > 0);

  if (!started) {
    return <Prelander onStart={() => setStarted(true)} />;
  }

  if (submitted) {
    return (
      <div
        className="min-h-screen relative overflow-hidden bg-white"
        data-testid="quiz-thank-you"
      >
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.04]"
          style={{ background: 'radial-gradient(circle, #2E7CF6 0%, transparent 70%)' }}
        />
        <div className="relative z-10 max-w-3xl mx-auto px-6 pt-20 pb-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#2E7CF6]/10 border border-[#2E7CF6]/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-7 h-7 text-[#2E7CF6]" />
          </div>
          <h1
            className="text-2xl sm:text-3xl text-slate-900 leading-tight mb-3 font-black tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Vielen Dank, {contactName} {contactLastName}!
          </h1>
          <p className="text-base text-slate-500 leading-relaxed mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Wir haben deine Angaben erhalten. Buche jetzt direkt deinen kostenlosen Ersttermin:
          </p>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 pb-10">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm" data-testid="calendly-embed">
            <InlineWidget
              url={CALENDLY_URL}
              prefill={{
                name: `${contactName} ${contactLastName}`.trim(),
                email: emailValue,
              }}
              styles={{ height: '660px', minWidth: '320px' }}
              pageSettings={{
                backgroundColor: 'ffffff',
                hideEventTypeDetails: false,
                hideLandingPageDetails: false,
                primaryColor: '2E7CF6',
                textColor: '0f172a',
              }}
            />
          </div>
          <div className="text-center mt-6">
            <a
              href="/"
              className="inline-block px-6 py-2.5 border border-slate-200 text-slate-400 text-sm rounded-lg hover:bg-slate-50 hover:text-slate-600 transition-all"
            >
              Zurück zur Startseite
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: '#f0f0f0' }} data-testid="quiz-page">
      <div className="fixed bottom-0 right-0 w-[700px] h-[700px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 60% 60%, rgba(63,167,219,0.12) 0%, rgba(33,89,117,0.08) 40%, transparent 70%)',
          filter: 'blur(50px)',
        }}
      />

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1.5 bg-black/5 z-50">
        <div
          className="h-full transition-all duration-500 ease-out rounded-r-full"
          style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #3FA7DB, #215975)' }}
          data-testid="quiz-progress"
        />
      </div>

      {/* Top bar */}
      <div className="fixed top-5 left-6 right-6 z-40 flex items-center justify-between">
        <span className="text-[#061620] font-bold text-lg tracking-[0.12em]" style={{ fontFamily: "'Lato', sans-serif" }}>
          VALORYX
        </span>
        <span className="text-sm text-[#8e8e8e]">{step + 1} / {total}</span>
      </div>

      {/* Nav arrows */}
      <div className="fixed bottom-6 right-6 z-40 flex items-center gap-2">
        <button onClick={goPrev} disabled={step === 0}
          className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center disabled:opacity-30 hover:bg-gray-50 transition-colors"
          data-testid="quiz-prev"
        >
          <ArrowLeft className="w-4 h-4 text-[#061620]" />
        </button>
        <button onClick={goNext} disabled={step >= total - 1}
          className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center disabled:opacity-30 hover:bg-gray-50 transition-colors"
          data-testid="quiz-next"
        >
          <ArrowRight className="w-4 h-4 text-[#061620]" />
        </button>
      </div>

      {/* Question */}
      <div className="min-h-screen flex items-start md:items-center justify-center px-6 py-20 md:py-24">
        <div className="w-full max-w-3xl" key={step}>
          <div className="mb-5 sm:mb-8">
            <div className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#061620] text-white text-xs font-bold shrink-0 mt-1">
                {current.number}
              </span>
              <h2 className="text-2xl md:text-[28px] font-medium text-[#061620] leading-snug" style={{ fontFamily: "'Lato', sans-serif" }}>
                {current.question}
              </h2>
            </div>
            {current.subtitle && (
              <p className="text-sm text-[#999] mt-3 ml-10">{current.subtitle}</p>
            )}
          </div>

          {/* Image cards */}
          {current.type === 'image-cards' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 ml-10">
              {current.options.map((opt) => {
                const selected = current.multi
                  ? (answers[current.id] || []).includes(opt.label)
                  : answers[current.id] === opt.label;
                return (
                  <ImageCard
                    key={opt.key}
                    opt={opt}
                    questionId={current.id}
                    selected={selected}
                    onClick={() => handleChoiceClick(current.id, opt.label, current.multi)}
                  />
                );
              })}
            </div>
          )}

          {/* Simple cards (no images) */}
          {current.type === 'cards' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 ml-10">
              {current.options.map((opt) => {
                const selected = current.multi
                  ? (answers[current.id] || []).includes(opt.label)
                  : answers[current.id] === opt.label;
                return (
                  <button
                    key={opt.key}
                    data-testid={`quiz-option-${current.id}-${opt.key}`}
                    onClick={() => handleChoiceClick(current.id, opt.label, current.multi)}
                    className={`relative rounded-xl px-5 py-4 text-left transition-all duration-200 border-2 ${
                      selected
                        ? 'border-[#3FA7DB] bg-white shadow-md'
                        : 'border-transparent bg-white/60 hover:bg-white hover:shadow-sm'
                    }`}
                  >
                    {selected && (
                      <div className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-[#3FA7DB] flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold mb-2 ${
                      selected ? 'bg-[#3FA7DB] text-white' : 'bg-[#e8e8e8] text-[#999]'
                    }`}>
                      {opt.key}
                    </span>
                    <p className="text-sm font-medium text-[#061620]">{opt.label}</p>
                  </button>
                );
              })}
            </div>
          )}

          {/* List options */}
          {current.type === 'list' && (
            <div className="space-y-2 ml-10 max-w-md">
              {current.options.map((opt) => {
                const selected = answers[current.id] === opt.label;
                return (
                  <button
                    key={opt.key}
                    data-testid={`quiz-option-${current.id}-${opt.key}`}
                    onClick={() => handleChoiceClick(current.id, opt.label, false)}
                    className={`w-full flex items-center gap-3 rounded-xl px-5 py-3.5 text-left transition-all duration-200 border-2 ${
                      selected
                        ? 'border-[#3FA7DB] bg-white shadow-sm'
                        : 'border-transparent bg-white/50 hover:bg-white'
                    }`}
                  >
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md text-xs font-bold ${
                      selected ? 'bg-[#3FA7DB] text-white' : 'bg-[#e0e0e0] text-[#999]'
                    }`}>
                      {opt.key}
                    </span>
                    <span className="text-base text-[#061620]">{opt.label}</span>
                    {selected && <Check className="w-4 h-4 text-[#3FA7DB] ml-auto" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* Text input */}
          {current.type === 'text' && (
            <div className="ml-10 max-w-lg">
              <input
                type="text"
                data-testid="quiz-text-input"
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && textValue.trim() && handleOk()}
                placeholder={current.placeholder}
                className="w-full bg-transparent border-b-2 border-[#3FA7DB]/30 focus:border-[#3FA7DB] outline-none text-lg py-3 text-[#061620] placeholder:text-[#c0c0c0] transition-colors"
                autoFocus
              />
            </div>
          )}

          {/* Email input (required) */}
          {current.type === 'email' && (
            <div className="ml-10 max-w-lg">
              <input
                type="email"
                data-testid="quiz-email-input"
                value={emailValue}
                onChange={(e) => { setEmailValue(e.target.value); setErrors(prev => ({ ...prev, email: null })); }}
                onKeyDown={(e) => e.key === 'Enter' && handleOk()}
                placeholder={current.placeholder}
                className={`w-full bg-transparent border-b-2 outline-none text-lg py-3 text-[#061620] placeholder:text-[#c0c0c0] transition-colors ${
                  errors.email ? 'border-red-400' : 'border-[#3FA7DB]/30 focus:border-[#3FA7DB]'
                }`}
                autoFocus
                required
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-2" data-testid="email-error">{errors.email}</p>
              )}
              <p className="text-xs text-[#999] mt-3">* Pflichtfeld — wir senden dir deine persönliche Auswertung</p>
            </div>
          )}

          {/* Contact form (required) */}
          {current.type === 'contact' && (
            <div className="ml-10 max-w-lg space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#455661] mb-2">Vorname *</label>
                  <input
                    type="text"
                    data-testid="quiz-contact-name"
                    value={contactName}
                    onChange={(e) => { setContactName(e.target.value); setErrors(prev => ({ ...prev, name: null })); }}
                    placeholder="Max"
                    className={`w-full bg-transparent border-b-2 outline-none text-lg py-3 text-[#061620] placeholder:text-[#c0c0c0] transition-colors ${
                      errors.name ? 'border-red-400' : 'border-[#3FA7DB]/30 focus:border-[#3FA7DB]'
                    }`}
                    autoFocus
                    required
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#455661] mb-2">Nachname *</label>
                  <input
                    type="text"
                    data-testid="quiz-contact-lastname"
                    value={contactLastName}
                    onChange={(e) => { setContactLastName(e.target.value); setErrors(prev => ({ ...prev, lastName: null })); }}
                    placeholder="Mustermann"
                    className={`w-full bg-transparent border-b-2 outline-none text-lg py-3 text-[#061620] placeholder:text-[#c0c0c0] transition-colors ${
                      errors.lastName ? 'border-red-400' : 'border-[#3FA7DB]/30 focus:border-[#3FA7DB]'
                    }`}
                    required
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#455661] mb-2">Mobilnummer *</label>
                <input
                  type="tel"
                  data-testid="quiz-contact-phone"
                  value={contactPhone}
                  onChange={(e) => { setContactPhone(e.target.value); setErrors(prev => ({ ...prev, phone: null })); }}
                  onKeyDown={(e) => e.key === 'Enter' && contactName.trim() && contactLastName.trim() && contactPhone.trim() && handleSubmit()}
                  placeholder="+49 151 234 56789"
                  className={`w-full bg-transparent border-b-2 outline-none text-lg py-3 text-[#061620] placeholder:text-[#c0c0c0] transition-colors ${
                    errors.phone ? 'border-red-400' : 'border-[#3FA7DB]/30 focus:border-[#3FA7DB]'
                  }`}
                  required
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
              <p className="text-xs text-[#999]">* Pflichtfelder — damit wir dich mit deiner Auswertung kontaktieren können</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="ml-10 mt-4 sm:mt-8">
            {current.type === 'contact' ? (
              <button
                data-testid="quiz-submit"
                onClick={handleSubmit}
                disabled={submitting}
                className="px-8 py-3.5 rounded-xl text-white font-bold text-base transition-all disabled:opacity-40 hover:opacity-90 cursor-pointer"
                style={{ background: 'linear-gradient(180deg, #3FA7DB 0%, #215975 100%)' }}
              >
                {submitting ? 'Wird gesendet...' : 'Auswertung erhalten & Termin buchen'}
              </button>
            ) : current.type === 'text' ? (
              <button
                data-testid="quiz-ok"
                onClick={handleOk}
                disabled={!textValue.trim()}
                className="px-6 py-2.5 rounded-lg bg-[#061620] text-white text-sm font-bold hover:bg-[#0d2d47] transition-colors disabled:opacity-40 cursor-pointer flex items-center gap-2"
              >
                Ok <Check className="w-3.5 h-3.5" />
              </button>
            ) : current.type === 'email' ? (
              <button
                data-testid="quiz-ok"
                onClick={handleOk}
                className="px-6 py-2.5 rounded-lg bg-[#061620] text-white text-sm font-bold hover:bg-[#0d2d47] transition-colors cursor-pointer flex items-center gap-2"
              >
                Ok <Check className="w-3.5 h-3.5" />
              </button>
            ) : current.multi && canProceedMulti ? (
              <button
                data-testid="quiz-ok-multi"
                onClick={goNext}
                className="px-6 py-2.5 rounded-lg bg-[#061620] text-white text-sm font-bold hover:bg-[#0d2d47] transition-colors cursor-pointer flex items-center gap-2"
              >
                Ok <Check className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
